/**
 * Postman Collection v2.1 Export Service
 *
 * Converts Snailman collections to Postman v2.1.0 format for export.
 */

import type { Collection, Folder, CollectionRequest, HttpRequest, KeyValue } from '../../stores';
import { generateId } from '../utils';

// Postman Collection v2.1 Types
export interface PostmanCollection {
  info: PostmanInfo;
  item: PostmanItem[];
  variable?: PostmanVariable[];
}

interface PostmanInfo {
  _postman_id: string;
  name: string;
  description?: string;
  schema: string;
}

interface PostmanItem {
  name: string;
  description?: string;
  request?: PostmanRequest;
  response?: PostmanResponse[];
  item?: PostmanItem[]; // For folders
}

interface PostmanRequest {
  method: string;
  url: PostmanUrl;
  header?: PostmanHeader[];
  body?: PostmanBody;
  auth?: PostmanAuth;
  description?: string;
}

interface PostmanUrl {
  raw: string;
  protocol?: string;
  host?: string[];
  port?: string;
  path?: string[];
  query?: PostmanQueryParam[];
}

interface PostmanHeader {
  key: string;
  value: string;
  description?: string;
  disabled?: boolean;
}

interface PostmanQueryParam {
  key: string;
  value: string;
  description?: string;
  disabled?: boolean;
}

interface PostmanBody {
  mode: 'raw' | 'urlencoded' | 'formdata' | 'none';
  raw?: string;
  urlencoded?: PostmanFormParam[];
  formdata?: PostmanFormParam[];
  options?: {
    raw?: { language?: string };
  };
}

interface PostmanFormParam {
  key: string;
  value: string;
  description?: string;
  disabled?: boolean;
  type?: 'text' | 'file';
}

interface PostmanAuth {
  type: string;
  apikey?: PostmanAuthAttribute[];
  basic?: PostmanAuthAttribute[];
  bearer?: PostmanAuthAttribute[];
  noauth?: null[];
}

interface PostmanAuthAttribute {
  key: string;
  value: string;
  type?: string;
}

interface PostmanVariable {
  key: string;
  value: string;
  type?: string;
}

interface PostmanResponse {
  name: string;
  originalRequest?: PostmanRequest;
  status?: string;
  code?: number;
  header?: PostmanHeader[];
  body?: string;
}

// Request data map: maps request IDs to full HttpRequest data
export type RequestDataMap = Map<string, HttpRequest>;

/**
 * Export a collection to Postman v2.1.0 format
 */
export function exportToPostman(
  collection: Collection,
  requestDataMap?: RequestDataMap
): PostmanCollection {
  return {
    info: {
      _postman_id: generateId(),
      name: collection.name,
      description: collection.description,
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    item: [
      // Export folders
      ...collection.folders.map((folder) => convertFolder(folder, requestDataMap)),
      // Export root-level requests
      ...collection.requests.map((request) => convertRequest(request, requestDataMap)),
    ],
  };
}

/**
 * Convert a folder to Postman item format
 */
function convertFolder(folder: Folder, requestDataMap?: RequestDataMap): PostmanItem {
  return {
    name: folder.name,
    item: folder.requests.map((request) => convertRequest(request, requestDataMap)),
  };
}

/**
 * Convert a request to Postman item format
 */
function convertRequest(request: CollectionRequest, requestDataMap?: RequestDataMap): PostmanItem {
  // Try to get full request data from the map
  const fullRequest = requestDataMap?.get(request.id);

  if (fullRequest) {
    return {
      name: request.name,
      request: convertFullRequest(fullRequest),
      response: [],
    };
  }

  // Fallback: use only the basic data we have
  return {
    name: request.name,
    request: {
      method: request.method || 'GET',
      url: {
        raw: '',
        host: [''],
        path: [],
      },
      header: [],
    },
    response: [],
  };
}

/**
 * Convert full HttpRequest to Postman request format
 */
function convertFullRequest(request: HttpRequest): PostmanRequest {
  const postmanRequest: PostmanRequest = {
    method: request.method,
    url: convertUrl(request.url, request.params),
    header: convertHeaders(request.headers),
  };

  // Add body if present
  if (request.body && request.body.type !== 'none') {
    postmanRequest.body = convertBody(request.body);
  }

  // Add auth if present
  if (request.auth && request.auth.type !== 'none') {
    postmanRequest.auth = convertAuth(request.auth);
  }

  return postmanRequest;
}

/**
 * Parse and convert URL to Postman URL format
 */
function convertUrl(url: string, params?: KeyValue[]): PostmanUrl {
  const postmanUrl: PostmanUrl = {
    raw: url,
  };

  try {
    // Parse the URL
    const parsed = new URL(url);

    postmanUrl.protocol = parsed.protocol.replace(':', '');
    postmanUrl.host = parsed.hostname.split('.');

    if (parsed.port) {
      postmanUrl.port = parsed.port;
    }

    if (parsed.pathname && parsed.pathname !== '/') {
      postmanUrl.path = parsed.pathname.split('/').filter(Boolean);
    }

    // Add query params
    const queryParams: PostmanQueryParam[] = [];

    // From URL search params
    parsed.searchParams.forEach((value, key) => {
      queryParams.push({ key, value });
    });

    // From explicit params array
    if (params && params.length > 0) {
      params.forEach((param) => {
        if (param.key) {
          queryParams.push({
            key: param.key,
            value: param.value || '',
            description: param.description,
            disabled: !param.enabled,
          });
        }
      });
    }

    if (queryParams.length > 0) {
      postmanUrl.query = queryParams;

      // Update raw URL to include query params
      if (params && params.length > 0) {
        const enabledParams = params.filter(p => p.enabled && p.key);
        if (enabledParams.length > 0) {
          const urlObj = new URL(url.includes('?') ? url.split('?')[0] : url);
          enabledParams.forEach(p => {
            urlObj.searchParams.append(p.key, p.value || '');
          });
          postmanUrl.raw = urlObj.toString();
        }
      }
    }
  } catch {
    // If URL parsing fails, use raw URL with variable placeholders
    // Handle {{variable}} syntax
    postmanUrl.raw = url;
    postmanUrl.host = [url];

    // Still add params if present
    if (params && params.length > 0) {
      postmanUrl.query = params.map((param) => ({
        key: param.key,
        value: param.value || '',
        description: param.description,
        disabled: !param.enabled,
      }));
    }
  }

  return postmanUrl;
}

/**
 * Convert headers to Postman format
 */
function convertHeaders(headers?: KeyValue[]): PostmanHeader[] {
  if (!headers || headers.length === 0) {
    return [];
  }

  return headers
    .filter((h) => h.key) // Filter out empty headers
    .map((header) => ({
      key: header.key,
      value: header.value || '',
      description: header.description,
      disabled: !header.enabled,
    }));
}

/**
 * Convert body to Postman format
 */
function convertBody(body: NonNullable<HttpRequest['body']>): PostmanBody {
  switch (body.type) {
    case 'raw':
      const contentType = body.raw?.contentType || 'text';
      // Map content type to Postman language
      const languageMap: Record<string, string> = {
        json: 'json',
        xml: 'xml',
        text: 'text',
        html: 'html',
        javascript: 'javascript',
      };
      return {
        mode: 'raw',
        raw: body.raw?.content || '',
        options: {
          raw: {
            language: languageMap[contentType] || 'text',
          },
        },
      };

    case 'x-www-form-urlencoded':
      return {
        mode: 'urlencoded',
        urlencoded: (body.formData || []).map((param) => ({
          key: param.key,
          value: param.value || '',
          description: param.description,
          disabled: !param.enabled,
          type: 'text',
        })),
      };

    case 'form-data':
      return {
        mode: 'formdata',
        formdata: (body.formData || []).map((param) => ({
          key: param.key,
          value: param.value || '',
          description: param.description,
          disabled: !param.enabled,
          type: 'text',
        })),
      };

    default:
      return { mode: 'none' };
  }
}

/**
 * Convert auth to Postman format
 */
function convertAuth(auth: NonNullable<HttpRequest['auth']>): PostmanAuth {
  switch (auth.type) {
    case 'bearer':
      return {
        type: 'bearer',
        bearer: [
          { key: 'token', value: auth.bearer?.token || '', type: 'string' },
        ],
      };

    case 'basic':
      return {
        type: 'basic',
        basic: [
          { key: 'username', value: auth.basic?.username || '', type: 'string' },
          { key: 'password', value: auth.basic?.password || '', type: 'string' },
        ],
      };

    case 'api-key':
      return {
        type: 'apikey',
        apikey: [
          { key: 'key', value: auth.apiKey?.key || '', type: 'string' },
          { key: 'value', value: auth.apiKey?.value || '', type: 'string' },
          { key: 'in', value: auth.apiKey?.addTo || 'header', type: 'string' },
        ],
      };

    default:
      return { type: 'noauth', noauth: [] };
  }
}

/**
 * Download the collection as a JSON file
 */
export function downloadPostmanCollection(
  collection: Collection,
  requestDataMap?: RequestDataMap
): void {
  const postmanCollection = exportToPostman(collection, requestDataMap);
  const json = JSON.stringify(postmanCollection, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${collection.name.replace(/[^a-z0-9]/gi, '_')}.postman_collection.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build a request data map from open tabs
 * This allows us to export full request data for requests that are currently open
 */
export function buildRequestDataMapFromTabs(
  tabs: Array<{ requestId?: string; request: HttpRequest }>
): RequestDataMap {
  const map: RequestDataMap = new Map();

  tabs.forEach((tab) => {
    if (tab.requestId && tab.request) {
      map.set(tab.requestId, tab.request);
    }
  });

  return map;
}
